import { Spinner, Title4 } from '@common/components';
import { Toggle } from '@common/components/Toggle';
import { BlockSelectField } from '@common/components/FormElements';
import { AvailabilityForm as AvailabilityFormComponent } from '@features/DynamicPackagesConfigurationWizardNew/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@features/spa';
import { useTranslations } from '@lang';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { PackagesFormValues, SelectedExperienceType, ExperienceType } from '../../../../types';
import {
  DropdownGridWrapper,
  DropdownWrapper,
  SpaAvailabilityWrapper,
  SpaLabel,
} from './ExperienceTimeslotSelection.styles';
import {
  TitleWrapper,
  ToggleWrapper,
} from '../SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection.styles';
import { usePackageConfiguration } from '../../../../contexts';
import { getSelectedOptionAndExperienceId } from '../../../../helpers/getSelectedOptionAndExperienceId';
import { getEffectiveExperienceDate } from '../../../../helpers/getEffectiveExperienceDate';
import { formatOptionsForSelect } from '../../../../helpers/formatOptionsForSelect';
import { getSelectableOptions } from '../../../../helpers/getSelectableOptions';

type Props = {
  experiences: { optionGroupId: number; options: SelectedExperienceType[] };
  configurationGroups: ConfigurationGroupType[];
  experienceIndex: number;
  guestIndex?: number;
  selectedDayNumber?: number;
  numberOfGuests?: number;
  isSameTimeForAllGuests?: boolean;
  onToggleSameTime?: () => void;
};

export const MultipleOptionsExperienceTimeslotSelection: React.FC<Props> = ({
  experiences,
  configurationGroups,
  experienceIndex,
  guestIndex = 0,
  selectedDayNumber,
  numberOfGuests = 1,
  isSameTimeForAllGuests = false,
  onToggleSameTime,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, journeyParams } =
    usePackageConfiguration();
  const { t } = useTranslations();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();
  const delayAfter = getValues(`selectedExperiences.${experienceIndex}.delayAfter`);
  const minimumTimeslotQuantity = isSameTimeForAllGuests ? numberOfGuests : 1;
  const guests = watch('guests') || [];
  const guest = guests[guestIndex];

  // Use selectedDayNumber if provided by parent, otherwise fall back to experience dayNumber
  const dayNumber = experiences.options[0]?.dayNumber;
  const { formattedDate: effectiveExperienceDate, effectiveDayNumber } = getEffectiveExperienceDate(
    journeyParams.fromDate,
    selectedDayNumber,
    dayNumber
  );

  const { data: configGroups, isFetching } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    effectiveExperienceDate,
    isSameTimeForAllGuests ? numberOfGuests : 1,
    spaOpenTime,
    spaCloseTime
  );

  const selectableOptions = getSelectableOptions(configGroups, experiences);

  const groupName = t('overnightPackage.spa.options');

  const selectableOptionsFormatted = formatOptionsForSelect(selectableOptions);

  const experience = watch(`guests.${guestIndex}.experiences.${experienceIndex}` as any);

  const { selectedOption, selectedExperienceId, selectedExperienceValue } =
    getSelectedOptionAndExperienceId(
      selectableOptionsFormatted,
      experience?.optionId,
      selectableOptions,
      experiences
    );

  return selectableOptions ? (
    <>
      <Spinner isActive={isFetching} isBackgroundDisabled={true} />

      <TitleWrapper>
        <Title4>{groupName}</Title4>
        {numberOfGuests > 1 && onToggleSameTime && (
          <ToggleWrapper>
            <Toggle
              input={{
                value: isSameTimeForAllGuests,
                onChange: onToggleSameTime,
              }}
              label={t('overnightPackage.spa.sameOptionForGuests')}
            />
          </ToggleWrapper>
        )}
      </TitleWrapper>

      <SpaAvailabilityWrapper
        data-testid={`multiple-options-selection-guest-${guestIndex}-group-${experiences.optionGroupId}`}
      >
        {!isSameTimeForAllGuests && guest && (
          <SpaLabel>
            {guest.guestDetails?.title} {guest.guestDetails?.firstName}{' '}
            {guest.guestDetails?.lastName}
          </SpaLabel>
        )}
        <>
          <SpaLabel required>{t('tables.menuSelect')}</SpaLabel>
          <DropdownGridWrapper>
            <DropdownWrapper>
              <BlockSelectField
                options={selectableOptionsFormatted}
                onChange={(val) => {
                  setValue(`guests.${guestIndex}.experiences.${experienceIndex}`, {
                    ...experience,
                    optionId: Number(val.value),
                    startTime: undefined,
                    endTime: undefined,
                    visitDate: undefined,
                    dayNumber: undefined,
                  } as any);
                }}
                placeholder={t('spa.toggle.select')}
                value={selectedExperienceValue}
                useNativeDropdownOnMobile={false}
                isSearchable={false}
                menuPlacement={'auto'}
                noOptionsMessage={() => t('spa.noOptionsAvailable')}
              />
            </DropdownWrapper>
          </DropdownGridWrapper>
          {selectedOption && selectedExperienceId && (
            <DropdownGridWrapper>
              <AvailabilityFormComponent
                id={Number(selectedExperienceId)}
                entityType={selectedOption.type as ExperienceType}
                timeslots={selectedOption.timeslots || []}
                namePrefix={`guests.${guestIndex}.experiences.${experienceIndex}`}
                guestIndex={guestIndex}
                delayAfter={delayAfter}
                dayNumber={effectiveDayNumber}
                experienceIndex={experienceIndex}
                minimumTimeslotQuantity={minimumTimeslotQuantity}
                selectedDayFromParent={selectedDayNumber}
              />
            </DropdownGridWrapper>
          )}
        </>
      </SpaAvailabilityWrapper>
    </>
  ) : null;
};
